import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ExerciciosRelacionados from './ExerciciosRelacionados';

interface Props {
  exercicioNome?: string;
}

interface ShareOption {
  label: string;
  icon: string;
  color: string;
  action: () => void;
}

function ShareSection() {
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const pageUrl = `${window.location.origin}${location.pathname}`;
  const pageTitle = document.title || 'Exercício de PNL Gratuito — Bia Holmes';
  const shareText = `Acabei de praticar este exercício gratuito de PNL! Vale muito a pena experimentar 🧠✨`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: pageTitle, text: shareText, url: pageUrl });
      } catch {
        // cancelled
      }
    } else {
      setShowModal(true);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(pageUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const options: ShareOption[] = [
    {
      label: 'WhatsApp',
      icon: 'ri-whatsapp-line',
      color: 'bg-green-500 hover:bg-green-600',
      action: () => window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${pageUrl}`)}`, '_blank'),
    },
    {
      label: 'X (Twitter)',
      icon: 'ri-twitter-x-line',
      color: 'bg-charcoal-800 hover:bg-charcoal-700',
      action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`, '_blank'),
    },
    {
      label: 'Telegram',
      icon: 'ri-telegram-line',
      color: 'bg-sky-500 hover:bg-sky-600',
      action: () => window.open(`https://t.me/share/url?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareText)}`, '_blank'),
    },
  ];

  return (
    <>
      <div className="py-10 px-6 border-t border-gold-500/10 text-center">
        <p className="text-white/45 text-sm mb-4 font-light">
          Gostou? Compartilhe com alguém que precisa disso
        </p>
        <button
          onClick={handleNativeShare}
          className="inline-flex items-center gap-2 px-7 py-3 bg-white/8 border border-gold-500/20 text-white rounded-full text-sm font-semibold hover:bg-white/14 hover:border-gold-400/40 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-share-line text-gold-400"></i>
          Compartilhar este exercício
        </button>
      </div>

      {/* Modal de compartilhamento */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-charcoal-950/80 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          ></div>
          <div className="relative bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-charcoal-100 hover:bg-charcoal-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-charcoal-600 text-sm"></i>
            </button>

            <h3 className="font-display text-xl font-bold text-charcoal-900 mb-2">
              Compartilhar Exercício
            </h3>
            <p className="text-charcoal-500 text-sm mb-6 leading-relaxed">
              Ajude mais pessoas a descobrirem técnicas de PNL gratuitas!
            </p>

            <div className="space-y-3 mb-5">
              {options.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => { opt.action(); setShowModal(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 ${opt.color} text-white rounded-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer`}
                >
                  <i className={`${opt.icon} text-lg`}></i>
                  Compartilhar no {opt.label}
                </button>
              ))}
            </div>

            {/* Copiar link */}
            <div className="flex items-center gap-2 bg-charcoal-50 border border-charcoal-100 rounded-xl px-3 py-2.5">
              <span className="text-xs text-charcoal-500 flex-1 truncate">{pageUrl}</span>
              <button
                onClick={copyLink}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-900 text-white rounded-lg text-xs font-semibold hover:bg-charcoal-800 transition-all whitespace-nowrap cursor-pointer"
              >
                {copied ? (
                  <><i className="ri-check-line text-green-400"></i> Copiado!</>
                ) : (
                  <><i className="ri-link-m"></i> Copiar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function LeadCaptureForm({ exercicioNome = 'este exercício' }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const slug = location.pathname.replace('/exercicio-gratis/', '');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    data.append('name', (form.elements.namedItem('name') as HTMLInputElement).value);
    data.append('exercicio', exercicioNome);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6tdqcdm9vk3c28i5h8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setTimeout(() => {
          navigate(`/obrigado-exercicio?de=${encodeURIComponent(slug)}`);
        }, 1800);
      } else {
        setError('Algo deu errado. Tente novamente.');
      }
    } catch {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-gradient-to-br from-charcoal-900 to-charcoal-950 py-16 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-gold-500/15 border border-gold-500/30 flex items-center justify-center mx-auto mb-5">
          <i className="ri-checkbox-circle-fill text-3xl text-gold-400"></i>
        </div>
        <h3 className="font-display text-2xl font-bold text-white mb-3">
          Perfeito! Você está na lista.
        </h3>
        <p className="text-white/50 leading-relaxed font-light text-sm">
          Redirecionando para os próximos exercícios recomendados...
        </p>
        <div className="mt-4 flex justify-center">
          <div className="w-6 h-6 border-2 border-gold-400/40 border-t-gold-400 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <section className="bg-gradient-to-br from-charcoal-900 to-charcoal-950 relative overflow-hidden">
      {/* Ornamento */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[200px] bg-gold-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-16 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-500/10 border border-gold-500/20 rounded-full text-gold-400 text-xs font-semibold tracking-widest uppercase mb-5">
            <i className="ri-mail-send-line"></i>
            Conteúdo exclusivo gratuito
          </div>
          <h2 className="font-display text-3xl font-bold text-white mb-3 leading-tight">
            Gostou? Receba mais técnicas<br />
            <span className="text-gold-400">diretamente no seu e-mail</span>
          </h2>
          <p className="text-white/55 font-light leading-relaxed text-sm">
            Novos exercícios de PNL, dicas de transformação e conteúdos exclusivos — sem spam, apenas valor.
          </p>
        </div>

        {/* Form */}
        <form
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white/5 border border-gold-500/15 rounded-2xl p-8 backdrop-blur-sm"
        >
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="lead-name" className="block text-xs font-semibold text-white/60 tracking-wide uppercase mb-2">
                Seu nome
              </label>
              <input
                id="lead-name"
                name="name"
                type="text"
                required
                placeholder="Como posso te chamar?"
                className="w-full px-4 py-3 bg-white/8 border border-white/15 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold-400/60 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="lead-email" className="block text-xs font-semibold text-white/60 tracking-wide uppercase mb-2">
                Seu melhor e-mail
              </label>
              <input
                id="lead-email"
                name="email"
                type="email"
                required
                placeholder="email@exemplo.com"
                className="w-full px-4 py-3 bg-white/8 border border-white/15 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold-400/60 transition-colors"
              />
            </div>
          </div>

          {error && (
            <p className="text-rose-400 text-sm mb-4 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 font-bold rounded-xl hover:from-gold-600 hover:to-gold-800 transition-all text-sm tracking-wide whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Enviando...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-send-plane-line"></i>
                Quero receber conteúdos de PNL
              </span>
            )}
          </button>

          <p className="text-center text-white/25 text-xs mt-4">
            Sem spam · Cancele quando quiser · Seus dados estão seguros
          </p>
        </form>
      </div>

      <ShareSection />
      <ExerciciosRelacionados />
    </section>
  );
}
